import React, { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import api, { formatApiError } from "@/lib/api";
import { Zap, ArrowRight, Loader2, User, Building2 } from "lucide-react";
import { CYRA_LOGO } from "@/lib/brand";
import { toast } from "sonner";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

export default function Register() {
  const { register } = useAuth();
  const navigate = useNavigate();
  const [role, setRole] = useState("professional");
  const [form, setForm] = useState({ name: "", email: "", password: "", city: "Chennai", state: "Tamil Nadu",
    primary_sport: "Basketball", primary_role: "Referee", org_type: "Club" });
  const [catalog, setCatalog] = useState({ sports: [], config: {} });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => { api.get("/catalog").then((r) => setCatalog(r.data)).catch(() => {}); }, []);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true); setError("");
    try {
      await register({ ...form, email: form.email.trim().toLowerCase(), role });
      toast.success("Krida account created");
      navigate("/app");
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || err.message);
    } finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen bg-krida-bg krida-grid-bg flex items-center justify-center p-5">
      <div className="w-full max-w-lg">
        <Link to="/" className="flex items-center justify-center gap-3 mb-6">
          <img src={CYRA_LOGO} alt="CYRA" className="h-14 w-auto" />
          <div className="h-8 w-px bg-slate-300" />
          <div className="font-serif italic text-krida-crimson text-3xl leading-none">Krida</div>
        </Link>
        <div className="krida-card p-8">
          <div className="overline text-krida-emerald mb-2">Join the workforce OS</div>
          <h1 className="font-heading text-3xl font-bold uppercase tracking-tight mb-6">Create account</h1>

          <div className="grid grid-cols-2 gap-3 mb-6">
            {[["professional", "Professional", User], ["organisation", "Organisation", Building2]].map(([r, l, Ic]) => (
              <button key={r} type="button" data-testid={`role-${r}`} onClick={() => setRole(r)}
                className={`flex items-center gap-2 justify-center py-3 rounded-xl border font-semibold text-sm transition-all ${role === r ? "border-krida-crimson bg-krida-crimson/10 text-krida-crimson" : "border-white/10 text-slate-400 hover:bg-white/5"}`}>
                <Ic className="w-4 h-4" /> {l}
              </button>
            ))}
          </div>

          <form onSubmit={submit} className="space-y-4">
            <Field label={role === "organisation" ? "Organisation name" : "Full name"} testid="reg-name" value={form.name} onChange={(v) => set("name", v)} />
            <Field label="Email" type="email" testid="reg-email" value={form.email} onChange={(v) => set("email", v)} />
            <Field label="Password" type="password" testid="reg-password" value={form.password} onChange={(v) => set("password", v)} />
            <div className="grid grid-cols-2 gap-3">
              <Field label="City" testid="reg-city" value={form.city} onChange={(v) => set("city", v)} />
              <Field label="State" testid="reg-state" value={form.state} onChange={(v) => set("state", v)} />
            </div>

            {role === "professional" ? (
              <div className="grid grid-cols-2 gap-3">
                <Dropdown label="Sport" testid="reg-sport" value={form.primary_sport} onChange={(v) => set("primary_sport", v)}
                  options={catalog.sports.map((s) => s.name)} />
                <Dropdown label="Role" testid="reg-role" value={form.primary_role} onChange={(v) => set("primary_role", v)}
                  options={catalog.config?.professional_roles || ["Referee", "Head Coach"]} />
              </div>
            ) : (
              <Dropdown label="Organisation type" testid="reg-orgtype" value={form.org_type} onChange={(v) => set("org_type", v)}
                options={catalog.config?.org_types || ["Club", "Academy"]} />
            )}

            {error && <div data-testid="register-error" className="text-sm text-krida-crimson bg-rose-500/10 border border-rose-500/30 rounded-lg px-3 py-2">{error}</div>}
            <button data-testid="register-submit" disabled={busy} className="w-full py-3 rounded-xl bg-krida-crimson hover:bg-krida-crimsonDark font-semibold flex items-center justify-center gap-2 transition-colors disabled:opacity-60">
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <>Activate Krida Pass <ArrowRight className="w-4 h-4" /></>}
            </button>
          </form>
          <p className="text-center text-slate-500 text-sm mt-5">
            Already registered? <Link to="/login" className="text-krida-crimson font-semibold">Sign in</Link>
          </p>
        </div>
      </div>
    </div>
  );
}

function Field({ label, value, onChange, type = "text", testid }) {
  return (
    <div>
      <label className="overline text-slate-500 block mb-1.5">{label}</label>
      <input data-testid={testid} type={type} value={value} onChange={(e) => onChange(e.target.value)} required
        className="w-full px-4 py-3 rounded-xl bg-black/40 border border-white/10 focus:border-krida-crimson/60 outline-none transition-colors" />
    </div>
  );
}

function Dropdown({ label, value, onChange, options, testid }) {
  return (
    <div>
      <label className="overline text-slate-500 block mb-1.5">{label}</label>
      <Select value={value} onValueChange={onChange}>
        <SelectTrigger data-testid={testid} className="w-full bg-black/40 border-white/10 rounded-xl py-3 h-auto"><SelectValue /></SelectTrigger>
        <SelectContent className="bg-krida-card border-white/10 text-slate-100 max-h-72">
          {options.map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}
        </SelectContent>
      </Select>
    </div>
  );
}
